import { AxiosError } from 'axios';
import { Resolver } from '../../config';
import {
  ConversionResponse,
  ConversionService,
  UsdConversionResult,
  WalletBalanceResponse,
  WalletBalanceResult,
} from './conversionService';
import { CustomError, NetworkError } from '../../common/errors';
import Result from '../../common/result';
import cachedAxios from '../../utils/cachedAxios';
import { FLOW_ID_HEADER } from '../../common/constants';

export default class ConversionServiceImplementation implements ConversionService {
  private endpoint: string;

  constructor() {
    const serviceConfig = Resolver.getServiceConfig();
    this.endpoint = serviceConfig.conversionService.endpoint;
  }

  private static getErrorResult<TSuccess = boolean>(url: string, error: unknown): Result<CustomError, TSuccess> {
    const statusCode = (error as AxiosError)?.response?.status;
    return Result.failed(new NetworkError(`Network error calling ${url}`, statusCode || 500, error as Error));
  }

  async getUsdConversionRate(toSymbol: string): Promise<UsdConversionResult> {
    const serviceConfig = Resolver.getServiceConfig();
    const url = `${this.endpoint}?chainCurrencySymbol=${toSymbol}`;
    try {
      const conversionResult: ConversionResponse = await cachedAxios
        .request<ConversionResponse>({
          url,
          method: 'GET',
          retryConfig: {
            retries: serviceConfig.networkRetryConfig.defaultRetries,
          },
          // Temporarily removing flow ID from header as CORS policy is currently blocking it
          transformRequest: (data, headers) => {
            // eslint-disable-next-line no-param-reassign
            delete headers.common[FLOW_ID_HEADER];
            return data;
          },
        })
        .then((response) => response.data);
      return Result.successful(conversionResult.usdConversionRate);
    } catch (error) {
      return ConversionServiceImplementation.getErrorResult(url, error);
    }
  }

  async getWalletBalance(address: string, chain: string, chainNetwork: string): Promise<WalletBalanceResult> {
    const serviceConfig = Resolver.getServiceConfig();
    const url = new URL(`${this.endpoint}`);
    url.searchParams.append('address', address);
    url.searchParams.append('chain', chain);
    url.searchParams.append('chainNetwork', chainNetwork);
    try {
      const balanceResult: WalletBalanceResponse = await cachedAxios
        .request<WalletBalanceResponse>({
          url: url.toString(),
          method: 'GET',
          retryConfig: {
            retries: serviceConfig.networkRetryConfig.defaultRetries,
          },
          // Temporarily removing flow ID from header as CORS policy is currently blocking it
          transformRequest: (data, headers) => {
            // eslint-disable-next-line no-param-reassign
            delete headers.common[FLOW_ID_HEADER];
            return data;
          },
        })
        .then((response) => response.data);
      return Result.successful(balanceResult.balance);
    } catch (error) {
      return ConversionServiceImplementation.getErrorResult(url.toString(), error);
    }
  }
}
